import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AlertCircle, Check, Loader2, MessageSquare } from 'lucide-react'
import axios from 'axios'
import {
  getOwnerPhone,
  requestCodeError,
  requestOwnerPhoneCode,
  verifyCodeError,
  verifyOwnerPhone,
  type OwnerPhoneStatus,
} from '../lib/ownerPhone'

// "Your mobile for urgent texts" (BRIEF-D12 fix round): the one phone the
// SOC texts when something needs the owner. The owner enters it, we text a
// 6-digit code, the owner enters the code. Owner only: a member's status
// call is refused (403) and nothing is drawn. The full number is never shown
// back, only its last four digits.

type Load = { state: 'loading' } | { state: 'hidden' } | { state: 'error' } | { state: 'ready'; status: OwnerPhoneStatus }

const inputClass =
  'mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40'
const buttonClass =
  'tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-4 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50 transition-colors'

export function OwnerMobileSection() {
  const [load, setLoad] = useState<Load>({ state: 'loading' })
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const here = useRef<HTMLElement>(null)
  const { hash } = useLocation()

  const refresh = useCallback(async () => {
    try {
      setLoad({ state: 'ready', status: await getOwnerPhone() })
    } catch (err) {
      const code = axios.isAxiosError(err) ? err.response?.status : undefined
      setLoad(code === 403 ? { state: 'hidden' } : { state: 'error' })
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // The /soc/today card links here (#mobile): bring the section into view.
  useEffect(() => {
    if (hash === '#mobile' && load.state !== 'hidden') here.current?.scrollIntoView?.({ block: 'start' })
  }, [hash, load.state])

  const sendCode = useCallback(async () => {
    const number = phone.trim()
    if (!number || busy) return
    setBusy(true)
    setError(null)
    setDone(false)
    try {
      await requestOwnerPhoneCode(number)
      setCodeSentTo(number)
      setCode('')
    } catch (err) {
      setError(requestCodeError(err))
    } finally {
      setBusy(false)
    }
  }, [busy, phone])

  const confirm = useCallback(async () => {
    if (!codeSentTo || busy) return
    if (!/^\d{6}$/.test(code.trim())) {
      setError('The code is the 6 digits in our text.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await verifyOwnerPhone(codeSentTo, code.trim())
      setDone(true)
      setCodeSentTo(null)
      setCode('')
      setPhone('')
      await refresh()
    } catch (err) {
      setError(verifyCodeError(err))
    } finally {
      setBusy(false)
    }
  }, [busy, code, codeSentTo, refresh])

  if (load.state === 'hidden') return null

  return (
    <section
      id="mobile"
      ref={here}
      aria-label="Your mobile for urgent texts"
      data-testid="owner-mobile"
      className="mb-5 rounded-2xl border border-brass/40 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center gap-1.5">
        <MessageSquare className="w-4 h-4 text-brass" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-navy">Your mobile for urgent texts</h2>
      </div>
      <p className="mt-0.5 text-xs text-gray-500">
        When something needs you, your SOC texts this mobile, away from your email in case
        that is the target.
      </p>

      {load.state === 'loading' ? (
        <p className="mt-3 flex items-center gap-2 text-xs text-gray-400" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Checking your mobile…
        </p>
      ) : load.state === 'error' ? (
        <p className="mt-3 text-xs text-danger" role="alert">
          We could not check your mobile just now. You can still set it below.
        </p>
      ) : (
        <p className="mt-3 text-sm text-navy" data-testid="owner-mobile-status">
          {load.status.verified
            ? `Verified: your mobile ending ${load.status.last_four}. We can text you.`
            : load.status.stopped
              ? `You asked us to stop texting your mobile ending ${load.status.last_four}. Reply START to any of our texts so we can reach you again.`
              : load.status.last_four
                ? `Your mobile ending ${load.status.last_four} is not verified yet, so we cannot text you.`
                : 'We have no mobile for you, so we cannot text you.'}
        </p>
      )}

      {done && load.state === 'ready' && load.status.verified && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-success" role="status">
          <Check className="w-3.5 h-3.5" aria-hidden="true" /> Your mobile is verified.
        </p>
      )}

      <div className="mt-3">
        <label htmlFor="owner-mobile-number" className="block text-xs font-medium text-navy">
          Mobile number
        </label>
        <input
          id="owner-mobile-number"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+64 21 123 4567"
          className={inputClass}
        />
        <button
          type="button"
          onClick={() => void sendCode()}
          disabled={busy || !phone.trim()}
          className={`${buttonClass} mt-2`}
        >
          {busy && !codeSentTo && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          Text me a code
        </button>
      </div>

      {codeSentTo && (
        <div className="mt-3">
          <label htmlFor="owner-mobile-code" className="block text-xs font-medium text-navy">
            The 6-digit code we texted you
          </label>
          <input
            id="owner-mobile-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={inputClass}
          />
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={busy}
            className={`${buttonClass} mt-2`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            Confirm code
          </button>
        </div>
      )}

      {error && (
        <div
          className="mt-3 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-3 text-sm text-danger"
          role="alert"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </section>
  )
}
